import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { CustomLinkType, CustomLinkStyle } from './CustomLink.types'

export const customLinkStyles: CustomLinkStyle = {
  default: 'custom-link custom-link--standard',
  inline: 'custom-link custom-link--inline',
  disabled: 'custom-link--disabled',
  primary: 'custom-link--primary',
  secondary: 'custom-link--secondary',
  nothing: ''
}

function renderLinkComponent(pathname: string, extraAttrs: any, children: any) {
  const params = {
    pathname
  }

  return (
    <Link to={params} {...extraAttrs}>
      {children}
    </Link>
  )
}

function renderAnchorLink(href: string, extraAttrs: any, children: any) {
  return (
    <a href={href} {...extraAttrs}>
      {children}
    </a>
  )
}

const CustomLink = (props: CustomLinkType) => {
  const {
    href = '',
    className = '',
    children,
    textContent,
    inline = false,
    uppercase = true,
    colorScheme = 'primary',
    openInNewTab,
    id,
    rel,
    title,
    isDisabled,
    isExternalLink,
    noStyle,
    onClick = () => {}
  }: CustomLinkType = props.data || props

  const cssClasses: string[] = useMemo(() => {
    const getCustomLinkStyle = (noStyle?: boolean, inline?: boolean) => {
      if (noStyle) return customLinkStyles.nothing
      if (inline) return customLinkStyles.inline
      return customLinkStyles.default
    }

    return [
      getCustomLinkStyle(noStyle, inline),
      customLinkStyles[colorScheme as keyof typeof customLinkStyles],
      uppercase ? 'uppercase' : ''
    ]
  }, [noStyle, inline, colorScheme, uppercase])

  const extraAttrs: any = {
    id,
    rel,
    title,
    target: openInNewTab ? '_blank' : '_self'
  }

  function preventDefaultBehavior(ev: React.SyntheticEvent) {
    ev.preventDefault()
    ev.stopPropagation()
    ev.nativeEvent.preventDefault()
    ev.nativeEvent.stopImmediatePropagation()
  }

  if (isDisabled) {
    cssClasses.push(customLinkStyles.disabled)
    extraAttrs.onClick = preventDefaultBehavior
  } else {
    extraAttrs.onClick = () => {
      onClick()
    }
  }

  cssClasses.push(className)
  extraAttrs.className = cssClasses.join(' ')

  const externalReg = new RegExp('^(http|https)://', 'i')
  const anchorReg = new RegExp('^#')

  return (
    isExternalLink || href.match(externalReg) || href.match(anchorReg)
      ? renderAnchorLink
      : renderLinkComponent
  )(href, extraAttrs, textContent || children)
}

export default CustomLink
